// Under 1024 px the rail and list collapse into this menu (CONTRACT
// "Responsive"): the role-visible rows grouped, in a fixed sheet under the top bar.
// Escape and an outside click close it; focus is trapped while open and
// returns to the menu button on close.
//
// It also carries the two things the top bar cannot hold at 390: the
// workspace this browser is signed in to, and the source and market state
// chips. SHELL-004 asks for the workspace, source freshness, account and
// search to stay reachable from the shell, and at 390 the bar has room for
// the search and the account only, so those two live here rather than
// nowhere. The head names the sheet and gives a pointer a close control that
// is not the scrim.
//
// It closes itself when the window grows past the menu breakpoint, because
// above it the button that opened the menu is gone and the sheet would sit
// over a shell that already shows every destination.
import { useEffect, useRef, type CSSProperties } from 'react';
import { X } from '@phosphor-icons/react';
import type { AuthIdentity, WorkspaceRole } from '../api/types';
import { Button } from '../ui/Button';
import { trapFocus } from '../ui/focusTrap';
import { MenuDestinations } from './ContextList';
import { useShell } from './ShellContext';
import { StateChipButton, workspaceLabel, type StateChip } from './TopBar';
import { MENU_MAX_WIDTH, useNarrow } from './useNarrow';

const DISMISS_LAYER: CSSProperties = {
  position: 'absolute',
  inset: 0,
  margin: 0,
  padding: 0,
  border: 0,
  background: 'transparent',
};

export interface MobileMenuProps {
  readonly identity: AuthIdentity | null;
  readonly sources: StateChip;
  readonly accessRole?: WorkspaceRole | null;
}

export function MobileMenu({ identity, sources, accessRole }: MobileMenuProps) {
  const { menuOpen, setMenuOpen } = useShell();
  const narrow = useNarrow(MENU_MAX_WIDTH);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (menuOpen && !narrow) setMenuOpen(false);
  }, [menuOpen, narrow, setMenuOpen]);

  useEffect(() => {
    if (!menuOpen || !ref.current) return undefined;
    return trapFocus(ref.current, { onEscape: () => setMenuOpen(false) });
  }, [menuOpen, setMenuOpen]);

  if (!menuOpen || !narrow) return null;
  const close = () => setMenuOpen(false);
  return (
    <div className="menu-scrim">
      <button
        type="button"
        tabIndex={-1}
        aria-label="Dismiss destinations menu"
        style={DISMISS_LAYER}
        onClick={close}
      />
      <div
        ref={ref}
        className="menu-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Destinations"
        tabIndex={-1}
      >
        <div className="menu-head">
          <span>Destinations</span>
          <Button
            variant="icon"
            size="sm"
            aria-label="Close destinations menu"
            icon={<X />}
            onClick={close}
          />
        </div>
        <div className="menu-ws">
          <span className="menu-ws-nm">{workspaceLabel(identity)}</span>
          <span className="menu-ws-role">
            {identity ? `${identity.user.displayName}, ${identity.role}` : 'no session'}
          </span>
        </div>
        <div className="menu-chips">
          <StateChipButton chip={sources} onSelect={close} navigateTo="sources" />
        </div>
        <MenuDestinations onNavigate={close} role={accessRole ?? identity?.role ?? null} />
      </div>
    </div>
  );
}
